import React, { useMemo, useState } from "react";
import { authApi } from "../api/authApi";
import ActionButton from "../../../component/common/ActionButton";
import AccountIdField, { toAccount } from "../../../component/common/AccountIdField";
import PasswordField from "../../../component/common/PasswordField";
import {
    Box,
    Paper,
    Typography,
    Alert,
    Stack,
    FormLabel,
    InputAdornment, IconButton,
} from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

export default function LoginPage({ onLoginSuccess }) {
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [error, setError] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = useMemo(() => email.trim() && pw.trim() && !submitting, [email, pw, submitting]);

    const handleLogin = async () => {
        setError("");
        setSubmitting(true);
        try {
            // 브라우저 자동완성이 onChange 를 안 태우는 경우까지 감안해 전송 직전 한 번 더 정리.
            const data = await authApi.login(toAccount(email), pw);

            if (data?.token) {
                const token = data.token;
                // 서버는 항상 expiration 을 내려주지만, 누락 시 기본 세션 시간(60분)으로 폴백.
                const expiration = data.expiration
                    ? parseInt(data.expiration, 10)
                    : Date.now() + 60 * 60 * 1000;

                localStorage.setItem("auth_token", token);
                localStorage.setItem("auth_token_exp", String(expiration));
                onLoginSuccess?.();
            } else {
                setError("로그인 실패");
            }
        } catch (e) {
            /* 판단 기준은 하나다: <b>서버가 응답을 줬는가</b>.
             * status 가 있으면 서버까지 갔다 온 것이므로 네트워크는 멀쩡하다 — 서버가 준 말을 그대로 전한다.
             * status 가 없을 때만(응답 자체가 없음) 네트워크 문제다. 예전엔 이 구분이 없어
             * 비밀번호를 틀려도 "네트워크 오류" 가 떴다. */
            if (e?.status === 401) setError(e.message || "아이디 또는 비밀번호를 확인해주세요");
            else if (e?.status) setError(e.message || "로그인에 실패했습니다");
            else setError("서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요");
        } finally {
            setSubmitting(false);
        }
    };

    // 조합 중 Enter 는 '한글 확정' 이지 '로그인' 이 아니다. 이걸 안 가리면 아직 정제되지 않은
    // 값으로 로그인이 나가 "분명히 맞게 쳤는데 실패" 가 된다.
    const onEnter = (e) => {
        if (e.key === "Enter" && !e.nativeEvent.isComposing && canSubmit) handleLogin();
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 760 }}>
                {/* 상단 로고 영역 */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>

                    <Box component="img" src="/brand/logo.png" alt="ONNME" sx={{ height: 34 }} />

                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        mx: "auto",
                        width: "100%",
                        maxWidth: 520,
                        borderRadius: 3,
                        border: "1px solid rgba(0,0,0,0.08)",
                        p: { xs: 3, sm: 4 },
                    }}
                >
                    <Typography sx={{ fontSize: 26, fontWeight: 800, mb: 3 }}>로그인</Typography>

                    <Stack spacing={2.25}>
                        {error && <Alert severity="error">{error}</Alert>}

                        {/* ✅ 라벨은 위, 입력칸은 텍스트만 */}
                        <Box>
                            <FormLabel sx={{ fontSize: 13, color: "rgba(0,0,0,0.60)", mb: 0.75, display: "block" }}>
                                계정명
                            </FormLabel>
                            {/* 붙여넣기·습관으로 도메인을 같이 적어도 입력칸에는 영문 로컬파트만 남는다. */}
                            <AccountIdField
                                value={email}
                                onChange={setEmail}
                                onKeyDown={onEnter}
                                autoFocus
                            />
                        </Box>

                        <Box>
                            <FormLabel sx={{ fontSize: 13, color: "rgba(0,0,0,0.60)", mb: 0.75, display: "block" }}>
                                비밀번호
                            </FormLabel>


                            {/* 계정명과 같은 규칙 — 한/영 상태를 모른 채 친 한글은 눌린 영문 키로 되돌린다. */}
                            <PasswordField
                                size="small"
                                fullWidth
                                value={pw}
                                onChange={setPw}
                                onKeyDown={onEnter}
                                type={showPw ? "text" : "password"}
                                autoComplete="current-password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPw((v) => !v)}  // ✅ showPw 토글
                                                edge="end"
                                                aria-label="toggle password visibility"
                                            >
                                                {!showPw ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {/* 계정 생성도 비밀번호 재발급도 전부 관리자를 통한다 —
                            셀프 창구(가입/비밀번호 찾기)는 두지 않으므로 버튼만 남긴다. */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 1 }}>
                            <ActionButton
                                variant="primary"
                                onClick={handleLogin}
                                disabled={!canSubmit}
                                loading={submitting}
                                label={submitting ? "로그인 중..." : "로그인"}
                            />
                        </Box>
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
}